import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

export type PullQuoteProps = {
	// Content
	quote: string;
	quoteRichTextString?: string;
	authorName?: string;
	authorDetails?: string;

	// Configuration
	size?: 'small' | 'large';
	variant?: 'basic' | 'cutout';
	alignment?: 'start' | 'center';
};

export const PullQuote = ({
	quote,
	quoteRichTextString,
	authorName,
	authorDetails,
	size = 'small',
	variant = 'basic',
	alignment = 'start',
}: PullQuoteProps) => {
	const quoteComponent = quoteRichTextString ? (
		<p
			css={quoteStyling}
			className={size === 'large' && variant === 'basic' ? 'callout-lg' : 'callout'}
			dangerouslySetInnerHTML={{ __html: quoteRichTextString }}
		/>
	) : (
		<p
			css={quoteStyling}
			className={size === 'large' && variant === 'basic' ? 'callout-lg' : 'callout'}
		>
			{quote}
		</p>
	);
	return (
		<div
			css={[
				commonStyling,
				alignment === 'center' && centeredStyling,
				variant === 'cutout' && cutoutStyling,
			]}
		>
			<blockquote css={quoteContainerStyling}>{quoteComponent}</blockquote>
			{(authorName || authorDetails) && (
				<div>
					{authorName && (
						<p className="body-lg" css={authorNameStyling}>
							{authorName}
						</p>
					)}
					{authorDetails && (
						<p className="eyebrow-md" css={authorDetailsStyling}>
							{authorDetails}
						</p>
					)}
				</div>
			)}
		</div>
	);
};

const commonStyling = css({
	display: 'grid',
	gap: token('space.100'),
	paddingBlock: token('space.200'),
});
const centeredStyling = css({
	textAlign: 'center',
});
const cutoutStyling = css({
	gap: token('space.200'),
	marginBlock: '4.75rem 4rem',
	position: 'relative',
	background: wacToken('color.primary.saffron'),
	paddingInline: token('space.400'),
	paddingBlock: token('space.200'),
	'::before': {
		content: '',
		position: 'absolute',
		insetBlockStart: '-4.7rem',
		insetInlineStart: 0,
		width: '100%',
		height: '4.75rem',
		background: wacToken('color.primary.saffron'),
		clipPath: 'polygon(0% 25%, 82% 0%, 83.25% 85%, 100% 90%, 100% 100%, 0% 100%)',
	},
	'::after': {
		content: '',
		position: 'absolute',
		insetBlockEnd: '-3.9rem',
		insetInlineStart: 0,
		width: '100%',
		height: '4rem',
		background: wacToken('color.primary.saffron'),
		clipPath: 'polygon(0% 0%, 0% 25%, 15% 25%, 14.5% 75%, 100% 100%, 100% 0%)',
	},
	[media.only.sm]: {
		paddingInline: '3.5rem',
	},
	[media.only.md]: {
		paddingInline: '3.5rem',
	},
	[media.above.lg]: {
		paddingInline: '6.25rem',
		marginBlock: '5rem 4.5rem',
		'::before': {
			insetBlockStart: '-4.9rem',
			height: '5rem',
		},
		'::after': {
			insetBlockEnd: '-4.4rem',
			height: '4.5rem',
		},
	},
});
const quoteContainerStyling = css({
	margin: '0',
});
const quoteStyling = css({
	quotes: 'auto',
	margin: '0',
	'::before': {
		content: 'open-quote',
	},
	'::after': {
		content: 'close-quote',
	},
});
const authorNameStyling = css({
	fontWeight: '700 !important',
	margin: '0',
});
const authorDetailsStyling = css({
	margin: '0',
});
